import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useEffect, useRef, useState } from 'react';
import { useIsUsingKeyboardToNavigate } from '../hooks/useIsUsingKeyboardToNavigate';
import { getNavigationViewportBreakpoint, getNavigationViewportMediaQueryLists, } from './utils/viewport-breakpoints';
export const NavigationContext = createContext({
    isKeyboardNavigating: false,
    focusedNavigationElement: null,
    setFocusedNavigationElement: () => { },
    navigationIsOpen: false,
    setNavigationIsOpen: () => { },
    setActiveItemReference: () => { },
    activeItemReference: null,
    setActiveActionReference: () => { },
    activeActionReference: null,
    navigationViewportBreakpoint: 'sm',
    itemsRegistryRef: { current: {} },
    addItemToRegistry: () => { },
    removeItemFromRegistry: () => { },
});
export const NavigationProvider = ({ children, preSelectedItemReference, }) => {
    const [isKeyboardNavigating, setIsKeyboardNavigating] = useState(false);
    const focusedNavigationElement = useRef(null);
    const [navigationViewportBreakpoint, setNavigationViewportBreakpoint] = useState('xxs');
    const [navigationIsOpen, setNavigationIsOpen] = useState(false);
    const [activeItemReference, setActiveItemReference] = useState(null);
    const [activeActionReference, setActiveActionReference] = useState(null);
    const timeout = useRef(null);
    const changeActiveItemReference = (reference) => {
        if (timeout.current) {
            window.clearTimeout(timeout.current);
        }
        if (reference === activeItemReference) {
            return;
        }
        const useDelay = reference === null;
        timeout.current = window.setTimeout(() => {
            setActiveItemReference(reference);
        }, useDelay ? 300 : 0);
    };
    useIsUsingKeyboardToNavigate({ onChange: setIsKeyboardNavigating });
    useEffect(() => {
        const checkMatch = () => {
            setNavigationViewportBreakpoint(getNavigationViewportBreakpoint());
        };
        getNavigationViewportMediaQueryLists().forEach(({ mediaQueryList }) => {
            mediaQueryList.addEventListener('change', checkMatch);
        });
        checkMatch();
        return () => {
            getNavigationViewportMediaQueryLists().forEach(({ mediaQueryList }) => {
                mediaQueryList.removeEventListener('change', checkMatch);
            });
        };
    }, []);
    useEffect(() => {
        setNavigationIsOpen(navigationViewportBreakpoint === 'lg');
    }, [navigationViewportBreakpoint]);
    useEffect(() => {
        if (navigationIsOpen) {
            setActiveActionReference(null);
            if (preSelectedItemReference && ['xxs', 'xs', 'sm'].includes(navigationViewportBreakpoint)) {
                setActiveItemReference(preSelectedItemReference);
            }
        }
        else {
            setActiveItemReference(null);
        }
    }, [navigationIsOpen, preSelectedItemReference, navigationViewportBreakpoint]);
    useEffect(() => {
        if (activeActionReference) {
            setNavigationIsOpen(false);
        }
    }, [activeActionReference]);
    useEffect(() => {
        if (activeItemReference) {
            setActiveActionReference(null);
        }
    }, [activeItemReference]);
    const itemsRegistryRef = useRef({});
    const addItemToRegistry = (reference, label, href) => {
        itemsRegistryRef.current[reference] = { label, href };
    };
    const removeItemFromRegistry = (reference) => {
        delete itemsRegistryRef.current[reference];
    };
    return (_jsx(NavigationContext.Provider, { value: {
            isKeyboardNavigating,
            focusedNavigationElement: focusedNavigationElement.current,
            setFocusedNavigationElement: (element) => {
                focusedNavigationElement.current = element;
            },
            navigationIsOpen,
            setNavigationIsOpen,
            activeItemReference,
            setActiveItemReference: changeActiveItemReference,
            activeActionReference,
            setActiveActionReference,
            navigationViewportBreakpoint,
            itemsRegistryRef,
            addItemToRegistry,
            removeItemFromRegistry,
        }, children: children }));
};
